'use client';

import SortIcon from '@mui/icons-material/Sort';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  useTheme,
} from '@mui/material';
import { useId, useState } from 'react';

type Option = { label: string; value: string; icon?: React.ReactNode };

interface SelectFieldProps {
  label: string;
  value: string | string[];
  options: Option[];
  placeholder?: string;
  onChange: (_value: string | string[]) => void;
  variant?: 'select' | 'button';
  multiple?: boolean;
  sx?: SxProps;
}

export function SelectField({
  label,
  value,
  options,
  placeholder = 'Select',
  onChange,
  variant = 'select',
  multiple = false,
  sx,
}: SelectFieldProps) {
  const theme = useTheme();
  const labelId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const getSelectedLabels = () => {
    if (Array.isArray(value)) {
      return value.map((v) => options.find((opt) => opt.value === v)?.label || v).join(', ');
    }
    return options.find((opt) => opt.value === value)?.label || placeholder;
  };

  const _selectedLabel = getSelectedLabels();

  // ───────────────────────────────
  // BUTTON VARIANT
  // ───────────────────────────────
  if (variant === 'button') {
    return (
      <FormControl sx={{ ...sx }}>
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          variant="contained"
          startIcon={<SortIcon />}
          sx={{
            borderRadius: 999,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: theme.typography.button.fontSize,
            width: '100%',
            backgroundColor: theme.palette.brand.primary,
            '&:hover': {
              backgroundColor: theme.palette.brand.primary,
              opacity: 0.9,
            },
          }}
        >
          {label}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            elevation: 4,
            sx: {
              borderRadius: 2,
              mt: 1,
              background: 'white',
              boxShadow: '0px 6px 20px rgba(0,0,0,0.12), 0px 2px 6px rgba(0,0,0,0.04)',
              '& .MuiMenuItem-root': {
                py: 1,
                px: 2,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          {options.map((opt) => {
            const selected = multiple
              ? Array.isArray(value) && value.includes(opt.value)
              : value === opt.value;

            return (
              <MenuItem
                key={opt.value}
                selected={selected}
                onClick={() => {
                  if (multiple) {
                    const current = Array.isArray(value) ? value : [];
                    const newValues = selected
                      ? current.filter((v) => v !== opt.value)
                      : [...current, opt.value];
                    onChange(newValues);
                  } else {
                    onChange(opt.value);
                    setAnchorEl(null);
                  }
                }}
              >
                {multiple && <Checkbox checked={selected} sx={{ mr: 1 }} />}
                {opt.icon && (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{opt.icon}</Box>
                )}
                {opt.label}
              </MenuItem>
            );
          })}
        </Menu>
      </FormControl>
    );
  }

  // ───────────────────────────────
  // SELECT DROPDOWN VARIANT
  // ───────────────────────────────
  return (
    <FormControl sx={{ ...sx }}>
      <FormLabel
        id={labelId}
        sx={{
          mb: 0.75,
          fontSize: 14,
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {label}
      </FormLabel>

      <Select
        labelId={labelId}
        multiple={multiple}
        value={value}
        onChange={(e: SelectChangeEvent<string | string[]>) => onChange(e.target.value)}
        input={<OutlinedInput />}
        displayEmpty
        renderValue={(selected) => {
          if (Array.isArray(selected))
            return selected
              .map((val) => options.find((opt) => opt.value === val)?.label || val)
              .join(', ');
          return options.find((opt) => opt.value === selected)?.label || placeholder;
        }}
        MenuProps={{
          PaperProps: {
            elevation: 4,
            sx: {
              borderRadius: '12px',
              mt: 1,
              background: 'white',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.1), 0px 2px 6px rgba(0,0,0,0.05)',
              '& .MuiMenuItem-root': {
                py: 1,
                px: 2,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  color: theme.palette.primary.main,
                },
              },
            },
          },
        }}
        sx={{
          height: 44,
          borderRadius: '10px',
          backgroundColor: '#fff',
          '& fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.primary.dark,
          },
          '& .MuiSelect-select': {
            py: 0.75,
            px: 1.25,
            fontSize: '0.875rem',
          },
        }}
      >
        <MenuItem disabled value="">
          {placeholder}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {multiple && (
              <Checkbox
                checked={Array.isArray(value) && value.includes(opt.value)}
                sx={{ mr: 1 }}
              />
            )}
            {opt.icon && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{opt.icon}</Box>
            )}
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
